import React, { createContext, useContext, useState, useEffect } from 'react';
import { refreshTokenManually, AUTH_STATUS_EVENT } from '../services/api';
import { config } from '../config/env';
import axios from 'axios';

const API_BASE_URL = config.apiUrl;

// Create axios instance with defaults
const authApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 second timeout
});

// Helper to store auth data consistently
const storeAuthData = (data) => {
  const { token, refreshToken, uid, roles, displayName } = data;
  
  if (!token || !refreshToken) {
    throw new Error('Invalid authentication data: missing required tokens');
  }

  localStorage.setItem('token', token);
  localStorage.setItem('refreshToken', refreshToken);
  localStorage.setItem('userId', uid);
  if (roles) localStorage.setItem('userRoles', JSON.stringify(roles));
  if (displayName) localStorage.setItem('displayName', displayName);
};

// Helper to clear auth data consistently
const clearAuthData = () => {
  // localStorage.removeItem('token');
  // localStorage.removeItem('refreshToken');
  // localStorage.removeItem('userId');
  // localStorage.removeItem('userRoles');
  // localStorage.removeItem('displayName');
};

// Add request interceptor
authApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  if (localStorage.getItem('orgId')) {
    config.headers['X-Organization-ID'] = localStorage.getItem('orgId');
  }

  return config;
}, (error) => {
  return Promise.reject(error);
});

// Add response interceptor
// Note: This interceptor handles 401s for authApi requests (like /auth/verify)
// The main api.js interceptor handles 401s for other API requests
authApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If error is 401 and we haven't tried to refresh token yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      // Check if refresh token exists before attempting refresh
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        // No refresh token available, can't refresh
        clearAuthData();
        window.dispatchEvent(new CustomEvent(AUTH_STATUS_EVENT, {
          detail: {
            status: 'expired',
            message: 'Session expired. Please log in again.'
          }
        }));
        return Promise.reject(error);
      }
      
      try {
        const refreshResponse = await refreshTokenManually();
        const newToken = refreshResponse.token;
        
        if (!newToken) {
          throw new Error('No token returned from refresh');
        }
        
        // Update token in localStorage
        localStorage.setItem('token', newToken);
        
        // Update the authorization header
        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        
        // Retry the original request
        return authApi(originalRequest);
      } catch (refreshError) {
        // If refresh token fails, don't immediately clear - let the error propagate
        // The checkAuth function or AUTH_STATUS_EVENT handler will handle cleanup
        console.error('[authApi Interceptor] Token refresh failed:', refreshError);
        window.dispatchEvent(new CustomEvent(AUTH_STATUS_EVENT, {
          detail: {
            status: 'refresh_failed',
            message: 'Session refresh failed. Please log in again.'
          }
        }));
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Guards to prevent concurrent auth checks and rapid retries
  const checkingAuthRef = React.useRef(false);
  const lastCheckTimeRef = React.useRef(0);
  const MIN_CHECK_INTERVAL = 2000; // Minimum 2 seconds between checks

  // Function to check authentication status with retry logic
  const checkAuth = async (retryCount = 0) => {
    // Prevent concurrent checks
    if (checkingAuthRef.current) {
      console.log('[Auth] Auth check already in progress, skipping');
      return;
    }
    
    // Throttle checks to prevent rapid retries (but allow retries within the function)
    const now = Date.now();
    if (now - lastCheckTimeRef.current < MIN_CHECK_INTERVAL && retryCount === 0) {
      console.log('[Auth] Auth check throttled, skipping');
      return;
    }
    
    checkingAuthRef.current = true;
    lastCheckTimeRef.current = now;
    
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 1000; // 1 second

    try {
      const token = localStorage.getItem('token');
      const refreshToken = localStorage.getItem('refreshToken');

      if (!token && !refreshToken) {
        setIsAuthenticated(false);
        setUser(null);
        setLoading(false);
        return;
      }

      // If we have a refresh token but no access token, try to refresh
      if (!token && refreshToken) {
        try {
          const refreshResponse = await refreshTokenManually();
          storeAuthData(refreshResponse);
          setUser({
            uid: refreshResponse.uid,
            displayName: refreshResponse.displayName,
            roles: refreshResponse.roles
          });
          setIsAuthenticated(true);
          setError(null);
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
          clearAuthData();
          setIsAuthenticated(false);
          setUser(null);
          setError('Session expired. Please log in again.');
        }
        setLoading(false);
        return;
      }

      // Verify existing token with retry logic
      try {
        const response = await authApi.get('/auth/verify');
        const userData = response.data;
        
        // Update stored user data
        if (userData.uid) localStorage.setItem('userId', userData.uid);
        if (userData.roles) localStorage.setItem('userRoles', JSON.stringify(userData.roles));
        if (userData.displayName) localStorage.setItem('displayName', userData.displayName);
        
        setUser(userData);
        setIsAuthenticated(true);
        setError(null);
      } catch (verifyError) {
        console.error('Token verification failed:', verifyError);
        
        // If it's a 401, the interceptor will handle refresh - don't do it here too
        // This prevents duplicate refresh attempts
        if (verifyError.response?.status === 401) {
          // Wait a bit to see if interceptor refreshed the token
          await new Promise(resolve => setTimeout(resolve, 500));
          const newToken = localStorage.getItem('token');
          if (newToken && newToken !== token) {
            // Token was refreshed by interceptor, verify again
            try {
              const response = await authApi.get('/auth/verify');
              const userData = response.data;
              setUser(userData);
              setIsAuthenticated(true);
              setError(null);
              return;
            } catch (retryError) {
              // Still failed after refresh attempt
              console.error('Token verification failed after refresh:', retryError);
            }
          }
          // If we get here, refresh didn't work or wasn't attempted
          // Don't try to refresh again here - let the error propagate
          setIsAuthenticated(false);
          setUser(null);
          setError('Session expired. Please log in again.');
          return;
        }
        
        // Retry on network errors or 5xx errors (transient failures)
        const isTransientError = 
          !verifyError.response || 
          (verifyError.response.status >= 500 && verifyError.response.status < 600) ||
          verifyError.code === 'ECONNABORTED' ||
          verifyError.code === 'ERR_NETWORK';
        
        if (isTransientError && retryCount < MAX_RETRIES) {
          console.log(`Retrying authentication check (attempt ${retryCount + 1}/${MAX_RETRIES})...`);
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (retryCount + 1)));
          checkingAuthRef.current = false;
          return checkAuth(retryCount + 1);
        }
        
        // Token is invalid and not a transient error, try refresh only if not 401
        if (verifyError.response?.status !== 401) {
          try {
            const refreshResponse = await refreshTokenManually();
            storeAuthData(refreshResponse);
            setUser({
              uid: refreshResponse.uid,
              displayName: refreshResponse.displayName,
              roles: refreshResponse.roles
            });
            setIsAuthenticated(true);
            setError(null);
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
            clearAuthData();
            setIsAuthenticated(false);
            setUser(null);
            setError('Session expired. Please log in again.');
          }
        } else {
          // 401 error that interceptor couldn't handle
          setIsAuthenticated(false);
          setUser(null);
          setError('Session expired. Please log in again.');
        }
      }
    } catch (err) {
      console.error('Auth check failed:', err);
      clearAuthData();
      setIsAuthenticated(false);
      setUser(null);
      setError('Authentication failed. Please log in again.');
    } finally {
      setLoading(false);
      checkingAuthRef.current = false;
    }
  };

  // Handle login
  const login = async (credentials) => {
    try {
      setLoading(true);
      setError(null);

      const response = await authApi.post('/auth/login', credentials);
      const data = response.data;

      // Store auth data
      storeAuthData(data);

      setUser(data);
      setIsAuthenticated(true);
      setError(null);

      return data;
    } catch (err) {
      console.error('Login error:', err);
      clearAuthData();
      if (err.response?.status === 503) {
        const serverError = 'The backend server is currently offline. Please try again later.';
        setError(serverError);
        throw new Error(serverError);
      } else {
        const errorMsg = err.response?.data?.message || 'Login failed. Please try again.';
        setError(errorMsg);
        throw new Error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle logout
  const logout = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await authApi.post('/auth/logout');
      }
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      clearAuthData();
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  // Listen for authentication status changes
  useEffect(() => {
    const handleAuthStatusChange = (event) => {
      const { status, message } = event.detail;

      switch (status) {
        case 'refreshed':
          const userId = localStorage.getItem('userId');
          const displayName = localStorage.getItem('displayName');
          const roles = JSON.parse(localStorage.getItem('userRoles') || '[]');

          setUser({
            uid: userId,
            displayName,
            roles
          });
          setIsAuthenticated(true);
          setError(null);
          break;

        case 'expired':
        case 'invalid_format':
        case 'refresh_failed':
          // Don't immediately clear - check if tokens are actually gone
          // This prevents loops where refresh fails but tokens still exist
          const hasToken = localStorage.getItem('token') || localStorage.getItem('refreshToken');
          if (!hasToken) {
            clearAuthData();
            setIsAuthenticated(false);
            setUser(null);
            setError(message || 'Your session has expired. Please log in again.');
          } else {
            // Tokens still exist - might be transient error
            // Wait a bit before clearing to avoid immediate loop
            setTimeout(() => {
              const stillHasToken = localStorage.getItem('token') || localStorage.getItem('refreshToken');
              if (!stillHasToken) {
                clearAuthData();
                setIsAuthenticated(false);
                setUser(null);
                setError(message || 'Your session has expired. Please log in again.');
              }
            }, 2000);
          }
          break;

        case 'network_error':
          setError(message || 'Network error. Please check your connection.');
          break;

        case 'permission_denied':
          setError(message || 'Permission denied.');
          break;

        default:
          break;
      }
    };

    window.addEventListener(AUTH_STATUS_EVENT, handleAuthStatusChange);
    return () => {
      window.removeEventListener(AUTH_STATUS_EVENT, handleAuthStatusChange);
    };
  }, []);

  // Check authentication status on mount
  // Skip initial check in embedded mode if no token exists yet (token will be set by parent app)
  useEffect(() => {
    const isEmbedded = config.isEmbedded && window.self !== window.top;
    const hasToken = localStorage.getItem('token') || localStorage.getItem('refreshToken');
    
    // In embedded mode, only check auth if token already exists
    // Otherwise, wait for EmbeddedApp to set the token and call checkAuth
    if (!isEmbedded || hasToken) {
      checkAuth();
    } else {
      // In embedded mode without token, set loading to false to allow UI to render
      // EmbeddedApp will handle calling checkAuth after receiving credentials
      setLoading(false);
    }
  }, []);

  const value = {
    isAuthenticated,
    user,
    loading,
    error,
    login,
    logout,
    checkAuth,
    setError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;